/**
 * Responsive breakpoint utilities
 * Provides reactive breakpoint detection for mobile-first responsive design
 */

import { ref, computed, onMounted, onUnmounted } from 'vue'

export type Breakpoint = 'mobile' | 'tablet' | 'desktop'

export interface BreakpointState {
  isMobile: boolean
  isTablet: boolean
  isDesktop: boolean
  current: Breakpoint
  width: number
}

// Tailwind breakpoints
const BREAKPOINTS = {
  mobile: 0,
  tablet: 640, // sm
  desktop: 1024 // lg
} as const

export function useBreakpoints() {
  const width = ref(typeof window !== 'undefined' ? window.innerWidth : 1024)

  const isMobile = computed(() => width.value < BREAKPOINTS.tablet)
  const isTablet = computed(() => width.value >= BREAKPOINTS.tablet && width.value < BREAKPOINTS.desktop)
  const isDesktop = computed(() => width.value >= BREAKPOINTS.desktop)

  const current = computed<Breakpoint>(() => {
    if (isMobile.value) return 'mobile'
    if (isTablet.value) return 'tablet'
    return 'desktop'
  })

  const updateWidth = () => {
    width.value = window.innerWidth
  }

  onMounted(() => {
    updateWidth()
    window.addEventListener('resize', updateWidth)
  })

  onUnmounted(() => {
    window.removeEventListener('resize', updateWidth)
  })

  return {
    isMobile,
    isTablet,
    isDesktop,
    current,
    width,
    // Helper for conditional rendering
    only: (breakpoint: Breakpoint) => current.value === breakpoint,
    // Helper for minimum width
    atLeast: (breakpoint: Breakpoint) => {
      if (breakpoint === 'mobile') return true
      if (breakpoint === 'tablet') return !isMobile.value
      return isDesktop.value
    }
  }
}
